// src/components/fraud/BlacklistViewDrawer.js
// Read-only detail drawer for a blacklist entry.
// Uses actual Firestore schema: uid, email, phone, ip, reason, notes, active, createdAt, createdBy

import React, { useState, useCallback } from 'react';
import { IDENTIFIER_FIELDS, parseBlDate } from '../../hooks/useBlacklist';

// ─── Copy hook ────────────────────────────────────────────────────────────────
const useCopy = () => {
  const [copied, setCopied] = useState(null);
  const copy = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key); setTimeout(() => setCopied(null), 1800);
    });
  }, []);
  return { copied, copy };
};

// ─── Shared atoms (identical pattern to FraudLogDrawer) ──────────────────────
const CopyBtn = ({ text, label, copied, onCopy }) => {
  const isCopied = copied === text;
  return (
    <button
      title={isCopied ? 'Copied!' : `Copy ${label || ''}`}
      onClick={() => onCopy(text, text)}
      style={{ background: 'transparent', border: 'none', padding: '0 4px', cursor: 'pointer', color: isCopied ? '#22C55E' : 'var(--text-secondary)', fontSize: 12, verticalAlign: 'middle', flexShrink: 0 }}
    >
      {isCopied ? '✓' : '⧉'}
    </button>
  );
};

const Field = ({ label, value, mono = false, copyable = false, copy, copied }) => {
  if (value === null || value === undefined || value === '') return null;
  const str = String(value);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, color: 'var(--text-primary)', fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>
        <span style={{ flex: 1 }}>{str}</span>
        {copyable && copy && <CopyBtn text={str} label={label} copied={copied} onCopy={copy} />}
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--accent-lime)', borderBottom: '1px solid var(--divider)', paddingBottom: 6, marginBottom: 14, marginTop: 20 }}>
    {children}
  </div>
);

const StatusBadge = ({ active }) => (
  <span style={{ fontSize: 11, fontWeight: 700, color: active ? '#22C55E' : '#A6A3B8', background: active ? 'rgba(34,197,94,0.12)' : 'rgba(166,163,184,0.10)', border: `1px solid ${active ? 'rgba(34,197,94,0.3)' : 'rgba(166,163,184,0.2)'}`, borderRadius: 20, padding: '3px 10px' }}>
  {active ? 'Active' : 'Disabled'}
  </span>
);

const RawViewer = ({ data }) => {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button onClick={() => setOpen((o) => !o)} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--divider)', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 11, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
        {open ? '▲ Hide raw document' : '▼ Show raw document'}
      </button>
      {open && (
        <pre style={{ marginTop: 8, background: 'var(--bg-primary)', border: '1px solid var(--divider)', borderRadius: 8, padding: 12, fontSize: 11, color: 'var(--text-secondary)', overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

// ─── Main Drawer ──────────────────────────────────────────────────────────────
const BlacklistViewDrawer = ({ entry, open, onClose, onEdit, onToggle, onDelete }) => {
  const { copied, copy } = useCopy();
  if (!entry) return null;

  const fmtDate = (d) => {
    const date = d instanceof Date ? d : parseBlDate(d);
    if (!date) return '—';
    return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Collect all populated identifiers
  const populatedIds = IDENTIFIER_FIELDS.filter((f) => entry[f.key]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040, opacity: open ? 1 : 0, transition: 'opacity 0.25s ease', pointerEvents: open ? 'all' : 'none' }}
      />

      {/* Drawer panel */}
      <div
        id="blacklist-view-drawer"
        style={{ position: 'fixed', top: 0, right: 0, height: '100vh', width: 'min(500px, 92vw)', background: 'var(--bg-surface)', borderLeft: '1px solid var(--divider)', zIndex: 1050, transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.4)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
          <div>
            <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 700, fontSize: 15 }}>Blacklist Entry</h6>
            <small style={{ color: 'var(--text-secondary)', fontSize: 11 }}>ID: {entry._id}</small>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--divider)', borderRadius: 6, color: 'var(--text-primary)', width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* Status row */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
            <StatusBadge active={entry.active} />
          </div>

          {/* ── Identifiers ── */}
          <SectionTitle>Identifiers</SectionTitle>
          {populatedIds.length === 0 && (
            <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No identifiers found.</p>
          )}
          {populatedIds.map((f) => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: f.color, background: `${f.color}18`, border: `1px solid ${f.color}44`, borderRadius: 3, padding: '1px 6px', textTransform: 'uppercase' }}>
                  {f.label}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                <span style={{ flex: 1 }}>{entry[f.key]}</span>
                <CopyBtn text={entry[f.key]} label={f.label} copied={copied} onCopy={copy} />
              </div>
            </div>
          ))}

          {/* ── Details ── */}
          <SectionTitle>Details</SectionTitle>
          <Field label="Reason" value={entry.reason} />
          <Field label="Notes"  value={entry.notes} />

          {/* ── Metadata ── */}
          <SectionTitle>Metadata</SectionTitle>
          <Field label="Created At" value={fmtDate(entry.createdAt)} />
          <Field label="Created By" value={entry.createdBy} />

          {/* ── Raw Document ── */}
          <SectionTitle>Raw Document</SectionTitle>
          <RawViewer data={entry.rawDoc} />

          {/* ── Quick actions ── */}
          <SectionTitle>Actions</SectionTitle>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => { onClose(); setTimeout(() => onEdit(entry), 200); }} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--divider)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, padding: '7px 16px', cursor: 'pointer' }}>
              ✏️ Edit
            </button>
            <button onClick={() => { onClose(); setTimeout(() => onToggle(entry._id, entry.active), 200); }} style={{ background: entry.active ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)', border: `1px solid ${entry.active ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`, borderRadius: 8, color: entry.active ? '#F59E0B' : '#22C55E', fontSize: 12, fontWeight: 600, padding: '7px 16px', cursor: 'pointer' }}>
              {entry.active ? '⊘ Disable' : '✓ Enable'}
            </button>
            <button onClick={() => { onClose(); setTimeout(() => onDelete(entry), 200); }} style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#EF4444', fontSize: 12, fontWeight: 600, padding: '7px 16px', cursor: 'pointer' }}>
              🗑 Delete
            </button>
          </div>

          <div style={{ height: 32 }} />
        </div>
      </div>
    </>
  );
};

export default BlacklistViewDrawer;
